import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumbs from "../../../components/breadcrumbs";
import IntroText from "../../../components/introText/index";
import Navigation from "../../../components/Navigation/Navigation";
import Search from "../../../components/inputs/Search";

// Style
import {
  HeaderFilterField,
  HeaderFilterLabel,
  HeaderFiltersWrap,
  LeftInfo,
  RightInfo,
  TopCard,
  TopContent,
} from "../../../style/Competition";
import {
  TabContent,
  TabListWrapper,
  TabsWrapper,
  TabTitle,
  TopList,
} from "../../../style/Favorites";
import {
  MainWrapper,
  TopWrapper,
  MainWrapperFavorite,
} from "../../../style/DasboardMain";

// Images
import arrowDown from "../../../images/down.svg";
import closeDropdown from "../../../images/up.svg";
import placeholder from "../../../images/ic_logo.svg";

// Components
import CompetitionMatch from "../../../components/table/CompetitionMatch";
import BestPlayers from "../../../components/table/BestPlayers";
import BestTeams from "../../../components/table/BestTeams";
import TeamsTable from "../../../components/table/TeamsTable";
import { $apiV1, $apiV2 } from "../../../config/api";
import { getGroupedAndSortedParametrs } from "../../../helpers";
import {
  excludeAttackingTypesParameterGroups,
  excludeAttackingTypesParameters,
} from "../../../helpers/teamParameterGroups";
import { useLexicon } from "../../../hooks/useLexicon";
import useOutsideClick from "../../../hooks/useOutsideClick";
import {
  Dropdown,
  DropdownButton,
  DropdownContent,
  DropdownItem,
} from "../shared/ui/EventSharedStyles";
import { trackEntityView } from "../../../tracking";

const MATCH_PRESET_STORAGE_KEY = "competition_match_preset";

const Index = () => {
  const l = useLexicon();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingInfo, setLoadingInfo] = useState(false);
  const [info, setInfo] = useState("");
  const [bestPlayersInfo, setBestPlayersInfo] = useState();
  const [bestTeamsInfo, setBestTeamsInfo] = useState();

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [teamParams, setTeamParams] = useState([])

  const { id } = useParams();
  const [openSeasonDropdown, setOpenSeasonDropdown] = useState(false);
  const [openMatchPresetDropdown, setOpenMatchPresetDropdown] = useState(false);
  const [selectedMatchPresetId, setSelectedMatchPresetId] = useState(() => {
    const storedPresetId = Number(sessionStorage.getItem(MATCH_PRESET_STORAGE_KEY));
    return [1, 2, 3, 4, 5].includes(storedPresetId) ? storedPresetId : 1;
  });

  const seasonDropdownRef = useOutsideClick(() => {
    if (openSeasonDropdown) {
      setOpenSeasonDropdown(false);
    }
  });

  const matchPresetDropdownRef = useOutsideClick(() => {
    if (openMatchPresetDropdown) {
      setOpenMatchPresetDropdown(false);
    }
  });

  const matchPresetOptions = useMemo(
    () => [
        { id: 1, title: l(1066) },
        { id: 2, title: "Last Match" },
        { id: 3, title: l(1067) },
        { id: 4, title: l(1068) },
        { id: 5, title: l(1069) },
    ],
    [l]
  );

  const selectedMatchPreset =
    matchPresetOptions.find((option) => option.id === selectedMatchPresetId) ||
    matchPresetOptions[0];

  const sortedAvailableSeasons = useMemo(
    () =>
      [...(info?.available_seasons || [])].sort(
        (a, b) => (b?.year_season?.id || 0) - (a?.year_season?.id || 0)
      ),
    [info?.available_seasons]
  );

  const selectedSeason = useMemo(
    () => sortedAvailableSeasons.find((season) => String(season?.id) === String(id)),
    [id, sortedAvailableSeasons]
  );
  const selectedSeasonLabel =
    selectedSeason?.year_season?.years ||
    selectedSeason?.title ||
    info?.title ||
    l(453);

  const breadcrumbs = [
    {
      title: l(1),
      link: "/tournaments",
    },
    {
      title: loadingInfo ? l(312) : `${info?.title}`,
    },
  ];

  useEffect(() => {
    const getTeamParams = async () => {
      try {
        let params = null;
        if (sessionStorage.getItem('teamParameters')) {
          params = excludeAttackingTypesParameterGroups(
            JSON.parse(sessionStorage.getItem('teamParameters'))
          )
          sessionStorage.setItem('teamParameters', JSON.stringify(params));
        } else {
          const response = await $apiV1.get('/stats/parameters/team');
          const data = response.data?.results;
          if (data) {
            params = getGroupedAndSortedParametrs(
              excludeAttackingTypesParameters(data)
            );
          }
        }
        setTeamParams(params);
      } catch (e) { /* empty */ }
    }

    getTeamParams();
  }, [])

  const getMathesStat = async (
    match,
    params = excludeAttackingTypesParameterGroups(
      JSON.parse(sessionStorage.getItem('teamParameters')) || []
    ),
    idx,
  ) => {
    try {
      let allParams = []
      params.forEach(([_, value]) => {
        allParams = [...allParams, ...value]
      })
      allParams = allParams?.filter((param) => param.checked)
      const checkedParamsIds = allParams?.map((item) => item.id);
      const response = await $apiV1.get(`/stats/match_teams/${match.id}?params=${checkedParamsIds?.join(',')}`);
      return {
        ...match,
        home_team: {
          ...response.data.teams_stats.home_team,
          stats: response.data.teams_stats.home_team?.stats?.sort((a, b) => a.parameter_id - b.parameter_id)
        },
        away_team: {
          ...response.data.teams_stats.away_team,
          stats: response.data.teams_stats.away_team?.stats?.sort((a, b) => a.parameter_id - b.parameter_id)
        },
        idx
      };
    } catch (e) {
      console.log('get match stat err', e);
      return Promise.reject(match);
    } finally {
    }
  }

  const getInfo = async (id, page = 1) => {
    try {
      setLoadingInfo(true);
      const response = await $apiV1.get(`/statistics/season_opt/${id}`, {
        params: { page },
      });
      let data = { ...response.data };
      if (data?.matches?.length > 0) {
        return Promise
          .allSettled(data.matches.map((match, idx) => getMathesStat(match, teamParams, idx)))
          .then(res => {
            res.map(resPromise => {
              if (resPromise.status === 'fulfilled') {
                data.matches[resPromise.value.idx] = resPromise.value;
              }
            })
          })
          .finally(() => {
            setInfo(data);
            trackEntityView({
              entity: data?.competition || data,
              entityId: data?.competition?.id || id,
              entityLabel: data?.competition?.name || data?.title,
              entityType: "competition",
              sourceSurface: "competition_profile",
              metadata: {
                season_id: id,
                season_label: data?.title,
              },
            });
            setTotalPages(response?.data?.pages_total || 1);
            setLoadingInfo(false);
          });
      }
      setInfo(data);
      trackEntityView({
        entity: data?.competition || data,
        entityId: data?.competition?.id || id,
        entityLabel: data?.competition?.name || data?.title,
        entityType: "competition",
        sourceSurface: "competition_profile",
        metadata: {
          season_id: id,
          season_label: data?.title,
        },
      });
      setTotalPages(response?.data?.pages_total || 1);
      setLoadingInfo(false);
    } catch (error) {
      console.error("Error fetching players:", error);
      setLoadingInfo(false);
    } finally {
    }
  };

  const getBestPlayers = async (id) => {
    try {
      setLoading(true);
      const response = await $apiV2.get(`/platform/season/${id}/best_players`);
      setBestPlayersInfo(response?.data);
    } catch (error) {
      console.error("Error fetching players:", error);
    } finally {
      setLoading(false);
    }
  };

  const getBestTeams = async (id) => {
    try {
      setLoading(true);
      const response = await $apiV2.get(`/platform/season/${id}/best_teams`);
      setBestTeamsInfo(response?.data);
    } catch (error) {
      console.error("Error fetching players:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getInfo(id, currentPage);
    getBestPlayers(id);
    getBestTeams(id);
  }, [id, currentPage]);

  useEffect(() => {
    sessionStorage.setItem(MATCH_PRESET_STORAGE_KEY, String(selectedMatchPresetId));
  }, [selectedMatchPresetId]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleChangeSeason = (season) => {
    if (!season?.id) {
      return;
    }
    navigate(`/tournaments/competition/${season.id}`);
  };

  const teamsTable = [
    {
      id: 1,
      teamName: "Mostar Velez",
      p: 12,
      wins: 5,
      draw: 0,
      lose: 12,
      goals: "4:1",
      points: 29,
    },
    {
      id: 2,
      teamName: "Mostar Velez",
      p: 12,
      wins: 5,
      draw: 0,
      lose: 12,
      goals: "4:1",
      points: 29,
    },
    {
      id: 3,
      teamName: "Mostar Velez",
      p: 12,
      wins: 5,
      draw: 0,
      lose: 12,
      goals: "4:1",
      points: 29,
    },
  ];

  const [navigationOpen, setNavigationOpen] = useState(
    localStorage.getItem("navigationOpen")
  );

  useEffect(() => {
    const handleNavigationOpenChange = () => {
      setNavigationOpen(localStorage.getItem("navigationOpen"));
      console.log(localStorage.getItem("navigationOpen"), "ss");
    };

    window.addEventListener("navigationOpenChange", handleNavigationOpenChange);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener(
        "navigationOpenChange",
        handleNavigationOpenChange
      );
    };
  }, []);

  return (
    <MainWrapper>
      <Navigation />
      <MainWrapperFavorite
        className={
          localStorage.getItem("navigationOpen") === "true"
            ? ""
            : "fullWidthPage"
        }
      >

        <>
          <TopWrapper>
            <IntroText
              smallTitle="Hello John"
              title={l(910)}
            />
            <Search />
          </TopWrapper>

          <Breadcrumbs items={breadcrumbs} />

          <TopCard>
            <TopContent>
              <LeftInfo>
                <img
                  src={info?.competition?.logo || placeholder}
                  alt={info?.competition?.logo || placeholder}
                />
              </LeftInfo>
              <RightInfo>
                <div className="competitionMeta">
                  <h3>{loadingInfo ? l(312) : info?.title}</h3>
                  <h5>{loadingInfo ? l(312) : info?.teams?.length} Clubs</h5>
                </div>
                <HeaderFiltersWrap>
                  <HeaderFilterField>
                    <HeaderFilterLabel>{l(453)}</HeaderFilterLabel>
                    <Dropdown ref={seasonDropdownRef}>
                      <DropdownButton
                        onClick={() =>
                          setOpenSeasonDropdown((previousState) => !previousState)
                        }
                        style={{
                          position: "relative",
                          width: "100%",
                          paddingRight: 10,
                          fontSize: 14,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {loadingInfo ? l(312) : selectedSeasonLabel}
                        <img
                          src={openSeasonDropdown ? closeDropdown : arrowDown}
                          alt={openSeasonDropdown ? closeDropdown : arrowDown}
                          style={{ width: 18, height: 18, right: 4 }}
                        />
                      </DropdownButton>
                      <DropdownContent $show={openSeasonDropdown}>
                        {sortedAvailableSeasons.length > 0 ? (
                          sortedAvailableSeasons.map((season) => (
                            <DropdownItem
                              key={season?.id}
                              selected={String(season?.id) === String(id)}
                              onClick={() => {
                                handleChangeSeason(season);
                                setOpenSeasonDropdown(false);
                              }}
                            >
                              {season?.title}{" "}
                              {season?.year_season?.years
                                ? ` - ${season?.year_season?.years}`
                                : ""}
                            </DropdownItem>
                          ))
                        ) : (
                          <DropdownItem>{l(464)}</DropdownItem>
                        )}
                      </DropdownContent>
                    </Dropdown>
                  </HeaderFilterField>

                  <HeaderFilterField>
                    <HeaderFilterLabel>{l(994)}</HeaderFilterLabel>
                    <Dropdown ref={matchPresetDropdownRef}>
                      <DropdownButton
                        onClick={() =>
                          setOpenMatchPresetDropdown((previousState) => !previousState)
                        }
                        style={{
                          position: "relative",
                          width: "100%",
                          paddingRight: 10,
                          fontSize: 14,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {selectedMatchPreset?.title || l(1066)}
                        <img
                          src={openMatchPresetDropdown ? closeDropdown : arrowDown}
                          alt={openMatchPresetDropdown ? closeDropdown : arrowDown}
                          style={{ width: 18, height: 18, right: 4 }}
                        />
                      </DropdownButton>
                      <DropdownContent $show={openMatchPresetDropdown}>
                        {matchPresetOptions.map((option) => (
                          <DropdownItem
                            key={option.id}
                            selected={option.id === selectedMatchPresetId}
                            onClick={() => {
                              setSelectedMatchPresetId(option.id);
                              setOpenMatchPresetDropdown(false);
                            }}
                          >
                            {option.title}
                          </DropdownItem>
                        ))}
                      </DropdownContent>
                    </Dropdown>
                  </HeaderFilterField>
                </HeaderFiltersWrap>
              </RightInfo>
            </TopContent>
          </TopCard>

          {/* Tabs */}
          <TabsWrapper>
            <TopList>
              <TabListWrapper className="competitionTabs">
                <TabTitle>{l(7)}</TabTitle>
                <TabTitle>{l(1009)}</TabTitle>
                <TabTitle>{l(1010)}</TabTitle>
                {/* <TabTitle>teams table</TabTitle> */}
              </TabListWrapper>
            </TopList>

            <TabContent>
              <CompetitionMatch
                table={info?.matches}
                load={loadingInfo}
                currentPage={currentPage}
                totalPages={totalPages}
                matchesCount={info?.matches_count}
                onPageChange={handlePageChange}
                teamParams={teamParams}
                matchPresetId={selectedMatchPresetId}
                id={id}
              />
            </TabContent>
            <TabContent>
              <BestPlayers table={bestPlayersInfo} info={info} />
            </TabContent>
            <TabContent>
              <BestTeams table={bestTeamsInfo} info={info} />
            </TabContent>
            <TabContent>
              <TeamsTable table={teamsTable} />
            </TabContent>
          </TabsWrapper>
        </>
      </MainWrapperFavorite>
    </MainWrapper>
  );
};

export default Index;
